import { forwardRef, type PropsWithoutRef, type Ref } from 'react';
import styled, { css } from 'styled-components';
import { mix } from 'polished';

import * as micIcon from '../Icon/icons/send-solid.icon';
import * as micSolidIcon from '../Icon/icons/square-solid.icon';
import type { NoChildrenProp, RefElement, TestIdProp, WithAttributes } from '../../types';
import { useI18n, useTestIds } from '../../hooks';
import Button from '../Button';
import Icon, { registerIcon } from '../Icon';
import { tryCatch, withTestIds } from '../../utils';
import { defaultThemeProp } from '../../theme';

import { getSpeechToTextButtonTestIds } from './SpeechToTextButton.test-ids';

registerIcon(micIcon, micSolidIcon);

const StyledSpeechToTextButton = styled(Button)(({ theme }) => {
  const activeColor = tryCatch(() =>
    mix(0.85, theme.base.palette['primary-background'], theme.base.palette.interactive)
  );

  return css`
    &[aria-pressed='true'] {
      background-color: ${activeColor};
    }
  `;
});

StyledSpeechToTextButton.defaultProps = defaultThemeProp;

export type SpeechToTextButtonProps = WithAttributes<
  'button',
  NoChildrenProp &
    TestIdProp & {
      /** Whether voice to text is active */
      active: boolean;
      /** Ref to the element */
      ref?: Ref<HTMLButtonElement>;
    }
>;

const SpeechToTextButton = forwardRef<
  RefElement<SpeechToTextButtonProps>,
  PropsWithoutRef<SpeechToTextButtonProps>
>(function SpeechToTextButton({ active, testId, ...restProps }, ref) {
  const t = useI18n();

  const testIds = useTestIds(testId, getSpeechToTextButtonTestIds);

  return (
    <StyledSpeechToTextButton
      ref={ref}
      data-testid={testIds.root}
      {...restProps}
      label={active ? t('stop_dictation') : t('dictate')}
      aria-pressed={active}
      variant='simple'
      icon
    >
      <Icon name={active ? 'mic-solid' : 'mic'} />
    </StyledSpeechToTextButton>
  );
});

export default withTestIds(SpeechToTextButton, getSpeechToTextButtonTestIds);
